import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  MessageSquare,
  MoreHorizontal,
  Briefcase,
  ListChecks,
  Truck,
  Users,
  Calendar,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useRole } from '../../context/RoleContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';

// Nav config per role — 4 tabs each.
//
// M2 (Sprint 286): every destination declared here MUST be an
// authorized route for the role that receives it. See `App.js` for the
// authoritative allowedRoles list on each route. The tests in
// `test_mobile_nav_role_matrix_iter286.py` re-verify this invariant.
const NAV_CONFIG = {
  owner: [
    { name: 'Today',  href: '/dispatch', icon: Truck          },
    { name: 'Jobs',   href: '/jobs',     icon: Briefcase      },
    { name: 'Inbox',  href: '/inbox',    icon: MessageSquare  },
    { name: 'More',   href: '/more',     icon: MoreHorizontal },
  ],
  technician: [
    { name: 'Today',   href: '/today',   icon: CalendarDays   },
    { name: 'Jobs',    href: '/jobs',    icon: Briefcase      },
    { name: 'Clients', href: '/clients', icon: Users          },
    { name: 'More',    href: '/more',    icon: MoreHorizontal },
  ],
  sales: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Leads',     href: '/leads',     icon: ListChecks      },
    { name: 'Inbox',     href: '/inbox',     icon: MessageSquare   },
    { name: 'More',      href: '/more',      icon: MoreHorizontal  },
  ],
  // M3 (Sprint 286): Marketing's Tab 3 was `/inbox`, but `/inbox`'s
  // route guard only allows owner/admin/sales — Marketing tapping their
  // own Inbox tab was deterministically redirecting to /login. Fixed by
  // pointing Tab 3 at `/calendar`, which Marketing IS authorized to
  // view. Do NOT restore /inbox here without also updating the route's
  // allowedRoles in App.js.
  marketing: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Leads',     href: '/leads',     icon: ListChecks      },
    { name: 'Calendar',  href: '/calendar',  icon: Calendar        },
    { name: 'More',      href: '/more',      icon: MoreHorizontal  },
  ],
};

// admin is identical to owner
NAV_CONFIG.admin = NAV_CONFIG.owner;

const MobileBottomNav = () => {
  const location = useLocation();
  const { role, isRoleHydrating } = useRole();
  const unreadCount = useUnreadCount();

  // M2 (Sprint 286): while role is genuinely unresolved, render a
  // non-interactive 4-slot skeleton. NEVER fall back to owner nav —
  // that briefly showed an Inbox tab to technicians on cellular, and a
  // tap in the flicker window sent them to /inbox → /login. The
  // skeleton keeps the layout stable and prevents any misrouting.
  if (isRoleHydrating) {
    return (
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="mobile-bottom-nav"
        aria-busy="true"
      >
        <div
          className="flex items-stretch justify-around h-14"
          data-testid="mobile-bottom-nav-skeleton"
        >
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col items-center justify-center flex-1 py-2 min-h-[56px]" aria-hidden="true">
              <div className="h-5 w-5 rounded bg-gray-200 animate-pulse" />
              <div className="h-2 w-8 mt-1.5 rounded bg-gray-100 animate-pulse" />
            </div>
          ))}
        </div>
      </nav>
    );
  }

  const navItems = NAV_CONFIG[role];
  // Unknown / unrecognized role: render nothing. Do NOT inherit owner nav.
  if (!navItems) {
    return null;
  }

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around h-14">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <NavLink
              key={item.name}
              to={item.href}
              data-testid={`mobile-nav-${item.name.toLowerCase()}`}
              className={cn(
                'flex flex-col items-center justify-center flex-1 py-2 transition-colors min-h-[56px] active:bg-gray-50',
                active ? 'text-[var(--cb-orange)]' : 'text-gray-500'
              )}
            >
              <div className="relative">
                <Icon className={cn('h-5 w-5', active && 'stroke-[2.5]')} />
                {item.href === '/inbox' && unreadCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1 leading-none pointer-events-none"
                    data-testid="inbox-unread-badge"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className={cn('text-[10px] mt-1 font-medium', active && 'font-semibold')}>
                {item.name}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

// Named export used by tests (and any future consumers that want the
// authoritative role→tabs table without importing the React component).
export { NAV_CONFIG };
export default MobileBottomNav;
